import { computed, reactive, ref, watch } from "vue";
import Swal from "sweetalert2";
import { useI18n } from "vue-i18n";
import warehouseService from "@/services/warehouse";
import supplierService from "@/services/supplier";
import {
  deletePurchase,
  exportPurchases,
  getPurchases,
  importPurchases,
  type Purchase,
} from "@/services/purchase";
import stateStore from "@/utils/store";

type PurchaseFilters = {
  payment_status: string;
  warehouse_id: string;
  supplier_id: string;
};

const DEFAULT_FILTERS: PurchaseFilters = {
  payment_status: "",
  warehouse_id: "",
  supplier_id: "",
};

export function usePurchase() {
  const { t, locale } = useI18n();
  const purchases = ref<Purchase[]>([]);
  const loading = ref(false);
  const searchQuery = ref("");
  const selectedIds = ref<number[]>([]);
  const warehouses = ref<{ id?: number; name?: string }[]>([]);
  const suppliers = ref<{ id?: number; name?: string }[]>([]);

  const filters = reactive<PurchaseFilters>({ ...DEFAULT_FILTERS });
  const pagination = reactive({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "30", "50", "100"],
    showTotal: (total: number, range: [number, number]) =>
      t("purchase_page.showing_total", { start: range[0], end: range[1], total }),
  });

  const normalizedParams = computed(() => ({
    search: searchQuery.value.trim() || undefined,
    payment_status: filters.payment_status || undefined,
    warehouse_id: filters.warehouse_id || undefined,
    supplier_id: filters.supplier_id || undefined,
  }));

  const fetchDropdownData = async () => {
    try {
      const [warehouseData, supplierData] = await Promise.all([
        warehouseService.getWarehouses(),
        supplierService.getSuppliers({ per_page: 1000 }),
      ]);

      warehouses.value = warehouseData;
      suppliers.value = supplierData.data;
    } catch (error) {
      console.error("Error fetching purchase filter data:", error);
    }
  };

  const fetchPurchases = async (page = pagination.current, pageSize = pagination.pageSize) => {
    loading.value = true;

    try {
      const response = await getPurchases({
        page,
        per_page: pageSize,
        ...normalizedParams.value,
      });

      purchases.value = response.data ?? [];
      pagination.current = response.current_page ?? page;
      pagination.pageSize = Number(response.per_page ?? pageSize);
      pagination.total = response.total ?? 0;
      selectedIds.value = selectedIds.value.filter((id) =>
        purchases.value.some((purchase) => purchase.id === id)
      );
    } catch (error) {
      console.error("Error fetching purchases:", error);
      Swal.fire({
        icon: "error",
        title: t("purchase_page.failed_to_load"),
        text: t("purchase_page.unable_to_load_purchases"),
      });
    } finally {
      loading.value = false;
    }
  };

  let searchTimer: ReturnType<typeof setTimeout> | null = null;

  watch(searchQuery, () => {
    if (searchTimer) {
      clearTimeout(searchTimer);
    }

    searchTimer = setTimeout(() => {
      fetchPurchases(1, pagination.pageSize);
    }, 300);
  });

  watch(
    () => ({ ...filters }),
    () => {
      fetchPurchases(1, pagination.pageSize);
    },
    { deep: true }
  );

  const resetFilters = () => {
    filters.payment_status = DEFAULT_FILTERS.payment_status;
    filters.warehouse_id = DEFAULT_FILTERS.warehouse_id;
    filters.supplier_id = DEFAULT_FILTERS.supplier_id;
  };

  const handleTableChange = (pager: { current?: number; pageSize?: number }) => {
    fetchPurchases(pager.current ?? 1, pager.pageSize ?? pagination.pageSize);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return t("purchase_page.not_available_short");

    return new Date(dateString).toLocaleString(locale.value === "km" ? "km-KH" : "en-GB", {
      timeZone: "Asia/Phnom_Penh",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  };

  const getDueAmount = (purchase: Purchase) => {
    const grandTotal = Number(purchase.grand_total || 0);
    const paid = Number(purchase.paid || 0);
    return Math.max(grandTotal - paid, 0);
  };

  const normalizePaymentStatus = (status?: string) => {
    const normalized = String(status || "").trim().toLowerCase();
    if (normalized === "due") return "pending";
    return normalized;
  };

  const normalizePurchaseStatus = (status?: string) =>
    String(status || "").trim().toLowerCase();

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleExport = async () => {
    try {
      const blob = await exportPurchases(normalizedParams.value);
      downloadBlob(blob, "purchases.csv");
    } catch (error) {
      console.error("Error exporting purchases:", error);
      Swal.fire({
        icon: "error",
        title: t("purchase_page.export_failed"),
        text: t("purchase_page.unable_to_export_purchase_list"),
      });
    }
  };

  const handleImport = async (event: Event) => {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];

    if (!file) return;

    Swal.fire({
      title: t("purchase_page.importing"),
      text: t("purchase_page.import_processing"),
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      const response = await importPurchases(file);

      Swal.fire({
        icon: "success",
        title: t("purchase_page.import_complete"),
        text: response?.message || t("purchase_page.purchases_imported_successfully"),
        timer: 1800,
        showConfirmButton: false,
      });

      await fetchPurchases(1, pagination.pageSize);
    } catch (error: any) {
      console.error("Error importing purchases:", error);
      Swal.fire({
        icon: "error",
        title: t("purchase_page.import_failed"),
        text: error?.response?.data?.message || t("purchase_page.failed_to_import_purchases"),
      });
    } finally {
      target.value = "";
    }
  };

  const deleteSinglePurchase = async (purchase: Purchase) => {
    if (!purchase.id) return;

    const result = await Swal.fire({
      title: t("purchase_page.delete_purchase_title"),
      text: t("purchase_page.delete_purchase_text", { ref: purchase.ref }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: t("purchase_page.confirm_delete"),
      cancelButtonText: t("common.cancel"),
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      Swal.fire({
        title: t("purchase_page.deleting"),
        text: t("purchase_page.please_wait"),
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      await deletePurchase(purchase.id);
      await fetchPurchases();

      Swal.fire({
        icon: "success",
        title: t("purchase_page.deleted"),
        text: t("purchase_page.purchase_removed", { ref: purchase.ref }),
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Error deleting purchase:", error);
      Swal.fire({
        icon: "error",
        title: t("purchase_page.delete_failed"),
        text: t("purchase_page.unable_to_delete_purchase"),
      });
    }
  };

  const deleteSelectedPurchases = async () => {
    if (!selectedIds.value.length) return;

    const result = await Swal.fire({
      title: t("purchase_page.delete_selected_purchases_title"),
      text: t("purchase_page.delete_selected_purchases_text", { count: selectedIds.value.length }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: t("purchase_page.confirm_delete_them"),
      cancelButtonText: t("common.cancel"),
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      Swal.fire({
        title: t("purchase_page.deleting"),
        text: t("purchase_page.please_wait"),
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      for (const id of selectedIds.value) {
        await deletePurchase(id);
      }

      selectedIds.value = [];
      await fetchPurchases(1, pagination.pageSize);

      Swal.fire({
        icon: "success",
        title: t("purchase_page.deleted"),
        text: t("purchase_page.selected_purchases_removed"),
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Error deleting selected purchases:", error);
      Swal.fire({
        icon: "error",
        title: t("purchase_page.delete_failed"),
        text: t("purchase_page.some_purchases_could_not_be_deleted"),
      });
    }
  };

  return {
    purchases,
    loading,
    searchQuery,
    selectedIds,
    warehouses,
    suppliers,
    filters,
    pagination,
    stateStore,
    fetchDropdownData,
    fetchPurchases,
    handleTableChange,
    resetFilters,
    handleExport,
    handleImport,
    deleteSinglePurchase,
    deleteSelectedPurchases,
    formatDate,
    getDueAmount,
    normalizePaymentStatus,
    normalizePurchaseStatus,
  };
}
